/**
 * Text Diff Service
 *
 * Compares original and improved text to find specific changes.
 * Returns changes in a format compatible with surgical corrections.
 */

import DiffMatchPatch from "diff-match-patch";

const dmp = new DiffMatchPatch();

/**
 * A single text improvement/change
 */
export interface TextChange {
  original: string;
  improved: string;
  context: string; // Text around the change for context
  position: number; // Character position in original text
}

/**
 * Find all changes between original and improved text
 */
export function findTextChanges(original: string, improved: string): TextChange[] {
  const diffs = dmp.diff_main(original, improved);
  dmp.diff_cleanupSemantic(diffs);

  const changes: TextChange[] = [];
  let position = 0;

  for (let i = 0; i < diffs.length; i++) {
    const [operation, text] = diffs[i];

    if (operation === 0) {
      // Equal - no change, just advance position
      position += text.length;
    } else if (operation === -1) {
      // Deletion - check if followed by insertion (replacement)
      const nextDiff = diffs[i + 1];

      if (nextDiff && nextDiff[0] === 1) {
        // This is a replacement (deletion + insertion)
        const originalText = text;
        const improvedText = nextDiff[1];

        // Get context (surrounding text)
        const contextStart = Math.max(0, position - 20);
        const contextEnd = Math.min(original.length, position + originalText.length + 20);
        const context = original.substring(contextStart, contextEnd);

        changes.push({
          original: originalText,
          improved: improvedText,
          context,
          position,
        });

        position += originalText.length;
        i++; // Skip the insertion since we handled it
      } else {
        // Pure deletion (text removed)
        const contextStart = Math.max(0, position - 20);
        const contextEnd = Math.min(original.length, position + text.length + 20);
        const context = original.substring(contextStart, contextEnd);

        changes.push({
          original: text,
          improved: "",
          context,
          position,
        });

        position += text.length;
      }
    } else if (operation === 1) {
      // Pure insertion (new text added)
      // Use character(s) before the insertion as anchor for search/replace
      // This allows us to search for "X" and replace with "Xnewtext"

      // Find a suitable anchor (word boundary or few chars before)
      let anchorStart = position;
      // Go back to find word start or max 10 chars
      while (anchorStart > 0 && position - anchorStart < 10 && !/\s/.test(original[anchorStart - 1])) {
        anchorStart--;
      }

      const anchor = original.substring(anchorStart, position);

      if (anchor.length > 0) {
        // Use anchor + insertion as a replacement
        changes.push({
          original: anchor,
          improved: anchor + text,
          context: original.substring(Math.max(0, anchorStart - 20), Math.min(original.length, position + 20)),
          position: anchorStart,
        });
      } else {
        // No anchor available (insertion at start) - use first char after as anchor
        const afterAnchor = original.substring(position, position + 5);
        if (afterAnchor.length > 0) {
          changes.push({
            original: afterAnchor,
            improved: text + afterAnchor,
            context: original.substring(0, Math.min(original.length, 30)),
            position: 0,
          });
        }
        // If no anchor at all, skip this insertion (rare edge case)
      }
      // Don't advance position for insertions
    }
  }

  return changes;
}

/**
 * Filter out only truly trivial changes (identical text, whitespace-only)
 * Also filters out footnote marker changes (superscript numbers that Word exports as plain text)
 * Trust the AI prompt to make the right decisions about what to change
 */
export function filterSignificantChanges(
  changes: TextChange[],
  _originalText?: string,
  _improvedText?: string
): TextChange[] {
  return changes.filter((change) => {
    // Skip if original and improved are exactly the same
    if (change.original === change.improved) {
      return false;
    }

    // Skip if they're the same after trimming whitespace
    if (change.original.trim() === change.improved.trim()) {
      return false;
    }

    // Skip footnote marker changes:
    // - Original is a small number (1-99) that AI wants to remove
    // - Or original ends with a number that AI wants to strip (e.g., "Civil1," → "Civil,")
    if (isFootnoteMarkerChange(change)) {
      return false;
    }

    // Keep everything else - trust the AI prompt
    return true;
  });
}

/**
 * Detect if a change is likely a footnote marker modification
 * Footnote markers in Word become plain numbers when exported as text
 * Examples: "Civil1," → "Civil,", "art. 5º2" → "art. 5º", "552," → "55,"
 */
function isFootnoteMarkerChange(change: TextChange): boolean {
  const { original, improved } = change;

  // Case 1: AI wants to remove a standalone small number (1-99)
  if (/^\d{1,2}$/.test(original) && improved === "") {
    return true;
  }

  // Case 2: Original ends with a digit that AI wants to strip
  // e.g., "Civil1," → "Civil," or "texto1" → "texto"
  const endsWithDigit = /^(.+?)(\d{1,2})([,.\s;:]?)$/.exec(original);
  if (endsWithDigit) {
    const [, prefix, , suffix] = endsWithDigit;
    const expectedImproved = prefix + suffix;
    if (improved === expectedImproved || improved === expectedImproved.trim()) {
      return true;
    }
  }

  // Case 3: AI changing a number that might be article+footnote confusion
  // e.g., "552," → "55," (552 could be art. 55 with footnote 2)
  // Only filter if the change is just removing trailing digits from a number
  const numberChange = /^(\d+)$/.exec(original);
  const improvedNumber = /^(\d+)$/.exec(improved);
  if (numberChange && improvedNumber) {
    // If original is longer and improved is a prefix of original, might be footnote
    if (original.startsWith(improved) && original.length - improved.length <= 2) {
      return true;
    }
  }

  return false;
}

/**
 * Get word context around a change for display purposes
 * Returns the full word containing the change
 */
export function getWordContext(
  change: TextChange,
  originalText: string
): { wordOriginal: string; wordImproved: string } {
  // If change is already word-sized or larger, return as-is
  if (change.original.includes(" ") || change.original.length >= 15) {
    return { wordOriginal: change.original, wordImproved: change.improved };
  }

  // Find word boundary before the change
  let startPos = change.position;
  while (startPos > 0 && !/\s/.test(originalText[startPos - 1])) {
    startPos--;
  }

  // Find word boundary after the change
  let endPos = change.position + change.original.length;
  while (endPos < originalText.length && !/\s/.test(originalText[endPos])) {
    endPos++;
  }

  // Extract the full word from original
  const wordOriginal = originalText.substring(startPos, endPos);

  // Reconstruct the improved word
  const prefix = originalText.substring(startPos, change.position);
  const suffix = originalText.substring(change.position + change.original.length, endPos);
  const wordImproved = prefix + change.improved + suffix;

  return { wordOriginal, wordImproved };
}

/**
 * Group nearby changes that might be part of the same edit
 */
export function groupNearbyChanges(changes: TextChange[], maxDistance: number = 5): TextChange[] {
  if (changes.length <= 1) return changes;

  const grouped: TextChange[] = [];
  let current = { ...changes[0] };

  for (let i = 1; i < changes.length; i++) {
    const change = changes[i];
    const gap = change.position - (current.position + current.original.length);

    if (gap <= maxDistance && gap >= 0) {
      // Merge with current
      // Get the text between the two changes from original
      const betweenText = " "; // Simplified - in real scenario would get actual text
      current.original += betweenText + change.original;
      current.improved += betweenText + change.improved;
      current.context = current.context; // Keep first context
    } else {
      // Start new group
      grouped.push(current);
      current = { ...change };
    }
  }
  grouped.push(current);

  return grouped;
}

/**
 * Convert TextChange to a format compatible with CorrectionItem
 * for reusing the existing UI and application logic
 */
export interface ImprovementItem {
  original: string;
  improved: string;
  context: string;
  position: number;
}

/**
 * Get a summary of changes
 */
export function getChangesSummary(changes: TextChange[]): string {
  const replacements = changes.filter((c) => c.original && c.improved).length;
  const deletions = changes.filter((c) => c.original && !c.improved).length;
  const insertions = changes.filter((c) => !c.original && c.improved).length;

  const parts: string[] = [];
  if (replacements > 0) parts.push(`${replacements} substituição(ões)`);
  if (deletions > 0) parts.push(`${deletions} remoção(ões)`);
  if (insertions > 0) parts.push(`${insertions} inserção(ões)`);

  return parts.join(", ") || "Nenhuma alteração";
}

/**
 * Create a visual diff for display (HTML with highlights)
 */
export function createVisualDiff(original: string, improved: string): string {
  const diffs = dmp.diff_main(original, improved);
  dmp.diff_cleanupSemantic(diffs);

  let html = "";
  for (const [operation, text] of diffs) {
    const escaped = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\n/g, "<br>");

    if (operation === -1) {
      html += `<del style="background:#ffcccb;text-decoration:line-through;">${escaped}</del>`;
    } else if (operation === 1) {
      html += `<ins style="background:#90EE90;text-decoration:none;">${escaped}</ins>`;
    } else {
      html += escaped;
    }
  }

  return html;
}
